import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import { Table } from '@wf/components';

import { ArrayFields, FeeFields, FeeTabFields } from '../../../License/types/fieldEnums';
import groupByBillNumber from '../../../License/helpers/groupByBillNumber';
import TableWrapper from '../../../../components/TableWrapper';
import TableFooter from '../../../../components/TableFooter';
import ComparisonTableCell from '../ComparisonTableCell';
import FeeComparisonFooter from './FeeComparisonFooter';
import AmountField from './AmountField';
import StatusCell from './StatusCell';

import useComparisonParams from '../../hooks/useComparisonParams';
import useListItems from '../../hooks/useListItems';

import { nationalCurrencySelector } from '../../../../store/configs/selectors';
import { versionComparisonSelector } from '../../../../store/audit/selectors';
import { formatDate } from '../../../../helpers/dateHelpers';
import formatNumber from '../../../../helpers/formatNumber';

import styles from './FeesComparison.module.scss';

function FeesComparison() {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const nationalCurrency = useSelector(nationalCurrencySelector);

  const { lpcoResponse, differenceMap } = useSelector(versionComparisonSelector);
  const { leftEpochMillis, rightEpochMillis } = useComparisonParams();
  const { amountToBePaid, totalAmount } = lpcoResponse;
  const [fees, feeStates] = useListItems(lpcoResponse.fees ?? [], ArrayFields.fees);

  const feesWithBillNumber = useMemo(
    () =>
      fees.map((fee) => {
        const path = `/${ArrayFields.fees}/${fee.id}/${FeeFields.billNumber}`;
        const billNumberNewValue =
          Number(leftEpochMillis) < Number(rightEpochMillis) && differenceMap[path]
            ? differenceMap[path]!.rightValue
            : fee.billNumber;

        return {
          ...fee,
          billNumber: billNumberNewValue as string | null,
        };
      }),
    [differenceMap, fees, leftEpochMillis, rightEpochMillis]
  );

  const groupedFees = useMemo(() => groupByBillNumber(feesWithBillNumber), [feesWithBillNumber]);

  return (
    <TableWrapper title={t('List of Fees and Charges')} isTabContent>
      <Table>
        <Table.Grid>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell className={styles.firstCell}>{t('#')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Invoice Number')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Invoice Date')} </Table.HeaderCell>
              <Table.HeaderCell>{t('Receipt Number')}</Table.HeaderCell>
              <Table.HeaderCell>{t('Receipt Date')}</Table.HeaderCell>
              <Table.HeaderCell className={styles.statusCell} align="right">
                {t('Status')}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body maxRows={100}>
            {groupedFees.map(({ receiptNumber, paymentDate, billNumber, invoiceDate, fees }, index: number) => {
              const id = fees[0]!.id;

              return (
                <Table.Expandable key={billNumber} isOpen>
                  <Table.Row rowState={feeStates[id]}>
                    <Table.Cell className={styles.firstCell}>{index + 1}</Table.Cell>
                    <ComparisonTableCell
                      path={`/${ArrayFields.fees}/${id}/${FeeFields.billNumber}`}
                      value={billNumber}
                    />
                    <ComparisonTableCell
                      path={`/${ArrayFields.fees}/${id}/${FeeFields.invoiceDate}`}
                      render={(value) => formatDate(value)}
                      value={invoiceDate}
                    />
                    <ComparisonTableCell
                      path={`/${ArrayFields.fees}/${id}/${FeeFields.receiptNumber}`}
                      value={receiptNumber}
                    />
                    <ComparisonTableCell
                      path={`/${ArrayFields.fees}/${id}/${FeeFields.paymentDate}`}
                      render={(value) => formatDate(value)}
                      value={paymentDate}
                    />
                    <Table.Cell className={styles.statusCell} align="right">
                      <StatusCell id={id} value={receiptNumber} />
                    </Table.Cell>
                  </Table.Row>
                  <Table.Expandable.Content>
                    <Table inner>
                      <Table.Grid>
                        <Table.Header>
                          <Table.Row>
                            <Table.HeaderCell className={styles.firstCell} />
                            <Table.HeaderCell>{t('Code')}</Table.HeaderCell>
                            <Table.HeaderCell>{t('Description')}</Table.HeaderCell>
                            <Table.HeaderCell align="right">{t('Base Amount')}</Table.HeaderCell>
                            <Table.HeaderCell align="right">{t('Rate')}</Table.HeaderCell>
                            <Table.HeaderCell align="right">{t('Amount')}</Table.HeaderCell>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body maxRows={10}>
                          {fees.map(({ id, feeCode, feeDescription, rate, amount, baseAmount }) => (
                            <Table.Row key={id}>
                              <Table.Cell className={styles.firstCell} />
                              <Table.Cell>{feeCode}</Table.Cell>
                              <Table.Cell>{feeDescription}</Table.Cell>
                              <Table.Cell align="right">
                                {formatNumber({ value: baseAmount, locale: language })}
                              </Table.Cell>
                              <Table.Cell align="right">
                                {formatNumber({
                                  value: rate,
                                  locale: language,
                                  maximumFractionDigits: 7,
                                  minimumFractionDigits: 2,
                                })}
                              </Table.Cell>
                              <Table.Cell align="right">{formatNumber({ value: amount, locale: language })}</Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table.Grid>
                    </Table>
                  </Table.Expandable.Content>
                </Table.Expandable>
              );
            })}
            <Table.Row className={styles.amountRow}>
              <div className={styles.amountRowContent}>
                <div>{t('Total Amount in {{currency}}', { currency: nationalCurrency })}</div>
                <AmountField name={FeeTabFields.totalAmount} value={totalAmount as number} />
              </div>
            </Table.Row>
            <Table.Row className={cn(styles.amountRow, styles.lastRow)}>
              <div className={styles.amountRowContent}>
                <div>{t('Total Amount To Be Paid')}</div>
                <AmountField name={FeeTabFields.amountToBePaid} value={amountToBePaid as number} />
              </div>
            </Table.Row>
          </Table.Body>
          <TableFooter page={1} totalCount={fees.length} withPagination={false} />
        </Table.Grid>
      </Table>
      <FeeComparisonFooter feeStates={feeStates} fees={feesWithBillNumber} />
    </TableWrapper>
  );
}

export default FeesComparison;
